import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  SimpleGrid,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  useColorModeValue,
  useToast,
  Alert,
  AlertIcon,
  AlertDescription,
  IconButton,
  InputGroup,
  InputRightElement,
  Divider,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon, AddIcon, EditIcon, DeleteIcon, CopyIcon } from "@chakra-ui/icons";
import { doc, getDoc, setDoc, serverTimestamp, Timestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import PageShell from "../layout/PageShell";

const EMAIL_CONFIG_COLLECTION = "emailConfig";

interface SmtpConfig {
  id: string;
  name: string;
  smtpServer: string;
  smtpPort: number;
  smtpUsername: string;
  smtpPassword: string;
  fromEmail: string;
  fromName?: string;
  isActive: boolean;
  createdAt?: any;
  lastUpdated?: any;
  updatedBy?: string;
}

interface EmailConfigData {
  activeSmtpId?: string;
  smtpConfigs?: SmtpConfig[];
  emailProvider?: "ses" | "brevo";
}

// Default Brevo configuration from screenshot
const getDefaultConfig = (): SmtpConfig => ({
  id: "default_brevo_smtp",
  name: "Master Password",
  smtpServer: "smtp-relay.brevo.com",
  smtpPort: 587,
  smtpUsername: "891b00001@smtp-brevo.com",
  smtpPassword: "", // User needs to enter their API key
  fromEmail: "", // User needs to enter their from email
  fromName: "",
  isActive: true,
  createdAt: new Date(),
  lastUpdated: new Date(),
  updatedBy: "system",
});

const EmailSettings: React.FC = () => {
  const toast = useToast();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  // Initialize with default config so it shows immediately
  const [smtpConfigs, setSmtpConfigs] = useState<SmtpConfig[]>([getDefaultConfig()]);
  const [activeSmtpId, setActiveSmtpId] = useState<string>("default_brevo_smtp");
  const [emailProvider, setEmailProvider] = useState<"ses" | "brevo">("ses");
  const [savingProvider, setSavingProvider] = useState(false);
  const [sesRegion, setSesRegion] = useState("ca-central-1");
  const [sesAccessKeyId, setSesAccessKeyId] = useState("");
  const [sesSecret, setSesSecret] = useState("");
  const [showSesSecret, setShowSesSecret] = useState(false);
  const [sesFromAddress, setSesFromAddress] = useState("");
  const [sesFromName, setSesFromName] = useState("NuagePOS");
  const [sesReplyTo, setSesReplyTo] = useState("");
  const [sesConfigurationSet, setSesConfigurationSet] = useState("nuage-pos-receipts");
  const [savingSes, setSavingSes] = useState(false);
  const [savingBounce, setSavingBounce] = useState(false);
  const [providerTab, setProviderTab] = useState(0);
  const [pendingProvider, setPendingProvider] = useState<"ses" | "brevo" | null>(null);
  const [confirmWord, setConfirmWord] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  
  // Form states
  const [editingConfig, setEditingConfig] = useState<SmtpConfig | null>(null);
  const [formName, setFormName] = useState("");
  const [smtpServer, setSmtpServer] = useState("");
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUsername, setSmtpUsername] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  // Test email states
  const [testEmail, setTestEmail] = useState("");
  const [testMessage, setTestMessage] = useState("This is a test email to verify SMTP configuration.");
  const [sendingTest, setSendingTest] = useState(false);
  
  // Webhook states
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookToken, setWebhookToken] = useState("");
  const [showWebhookToken, setShowWebhookToken] = useState(false);
  
  const { isOpen, onOpen, onClose } = useDisclosure();

  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.700", "white");
  const textSecondary = useColorModeValue("gray.500", "gray.400");
  const bgPage = useColorModeValue("gray.50", "gray.900");

  useEffect(() => {
    loadConfigs();
    loadWebhookConfig();
    loadSes();
  }, []);

  useEffect(() => {
    // Generate webhook URL based on Firebase project
    const projectId = "fiverrrms1"; // From firebase config
    const region = process.env.REACT_APP_FIREBASE_REGION || "northamerica-northeast1";
    const webhookEndpoint = `https://${region}-${projectId}.cloudfunctions.net/brevoWebhook`;
    setWebhookUrl(webhookEndpoint);
  }, []);

  const loadSes = async () => {
    try {
      const snap = await getDoc(doc(db, "sesConfig", "global"));
      const data = snap.exists() ? snap.data() : {};
      if (data.region) setSesRegion(data.region);
      setSesAccessKeyId(data.accessKeyId || "");
      setSesSecret(data.secretAccessKey || "");
      setSesFromAddress(data.fromAddress || "");
      setSesFromName(data.fromName || "NuagePOS");
      setSesReplyTo(data.replyTo || "");
      setSesConfigurationSet(data.configurationSet || "nuage-pos-receipts");
    } catch (error: any) {
      toast({ title: "Unable to load Amazon SES", description: error.message, status: "error", duration: 5000, isClosable: true });
    }
  };

  const handleSaveSes = async () => {
    setSavingSes(true);
    try {
      await setDoc(
        doc(db, "sesConfig", "global"),
        {
          region: sesRegion,
          accessKeyId: sesAccessKeyId.trim(),
          secretAccessKey: sesSecret.trim(),
          fromAddress: sesFromAddress.trim().toLowerCase(),
          fromName: sesFromName.trim(),
          replyTo: sesReplyTo.trim().toLowerCase(),
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.email || "",
        },
        { merge: true }
      );
      toast({ title: "Amazon SES settings saved", status: "success", duration: 3000, isClosable: true });
    } catch (error: any) {
      toast({ title: "Amazon SES settings not saved", description: error.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setSavingSes(false);
    }
  };

  const handleSaveBounce = async () => {
    const name = sesConfigurationSet.trim();
    if (!/^[A-Za-z0-9_-]{1,64}$/.test(name)) {
      toast({
        title: "Configuration set not saved",
        description: "Use the configuration set name created in Amazon SES. Letters, numbers, hyphens, and underscores only.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    setSavingBounce(true);
    try {
      await setDoc(
        doc(db, "sesConfig", "global"),
        {
          configurationSet: name,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.email || "",
        },
        { merge: true }
      );
      toast({ title: "Email bounce tracking saved", status: "success", duration: 3000, isClosable: true });
    } catch (error: any) {
      toast({ title: "Email bounce tracking not saved", description: error.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setSavingBounce(false);
    }
  };

  const loadConfigs = async () => {
    try {
      const configDocRef = doc(db, EMAIL_CONFIG_COLLECTION, "global");
      const configDoc = await getDoc(configDocRef);

      if (configDoc.exists()) {
        const data = configDoc.data() as EmailConfigData;
        const provider = data.emailProvider === "brevo" ? "brevo" : "ses";
        setEmailProvider(provider);
        if (data.emailProvider !== "ses" && data.emailProvider !== "brevo") {
          await setDoc(configDocRef, { emailProvider: "ses", lastUpdated: serverTimestamp() }, { merge: true });
        }
        const loadedConfigs = data.smtpConfigs || [];
        
        // If no configs exist, use default
        if (loadedConfigs.length === 0) {
          const defaultConfig = getDefaultConfig();
          defaultConfig.createdAt = Timestamp.now();
          defaultConfig.lastUpdated = Timestamp.now();
          defaultConfig.updatedBy = currentUser?.email || "system";
          
          // Save default to Firestore
          await setDoc(
            configDocRef,
            {
              smtpConfigs: [defaultConfig],
              activeSmtpId: defaultConfig.id,
              emailProvider: "ses",
              lastUpdated: serverTimestamp(),
            }
          );
          
          setSmtpConfigs([defaultConfig]);
          setActiveSmtpId(defaultConfig.id);
          setEmailProvider("ses");
        } else {
          setSmtpConfigs(loadedConfigs);
          setActiveSmtpId(data.activeSmtpId || loadedConfigs[0]?.id || "");
        }
      } else {
        // No config exists, create default
        const defaultConfig = getDefaultConfig();
        defaultConfig.createdAt = Timestamp.now();
        defaultConfig.lastUpdated = Timestamp.now();
        defaultConfig.updatedBy = currentUser?.email || "system";

        await setDoc(
          configDocRef,
          {
            smtpConfigs: [defaultConfig],
            activeSmtpId: defaultConfig.id,
            emailProvider: "ses",
            lastUpdated: serverTimestamp(),
          }
        );

        setSmtpConfigs([defaultConfig]);
        setActiveSmtpId(defaultConfig.id);
        setEmailProvider("ses");
      }
    } catch (error) {
      console.error("Error loading email config:", error);
      // Don't show error toast, just use default values that are already in state
      // The default config is already set in initial state, so UI will still work
    }
  };

  const loadWebhookConfig = async () => {
    try {
      const configDocRef = doc(db, EMAIL_CONFIG_COLLECTION, "global");
      const configDoc = await getDoc(configDocRef);

      if (configDoc.exists()) {
        const data = configDoc.data();
        if (data.webhookToken) {
          setWebhookToken(data.webhookToken);
        }
      }
    } catch (error) {
      console.error("Error loading webhook config:", error);
    }
  };

  const generateWebhookToken = () => {
    // Generate a random token
    const token = `brevo_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    setWebhookToken(token);
    return token;
  };

  const saveWebhookToken = async () => {
    try {
      const configDocRef = doc(db, EMAIL_CONFIG_COLLECTION, "global");
      const configDoc = await getDoc(configDocRef);

      if (configDoc.exists()) {
        await setDoc(
          configDocRef,
          {
            webhookToken: webhookToken,
            lastUpdated: serverTimestamp(),
          },
          { merge: true }
        );
      } else {
        await setDoc(configDocRef, {
          webhookToken: webhookToken,
          lastUpdated: serverTimestamp(),
        });
      }

      toast({
        title: "Success",
        description: "Webhook token saved successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error saving webhook token:", error);
      toast({
        title: "Error",
        description: "Failed to save webhook token",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCopyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    toast({
      title: "Copied",
      description: "Webhook URL copied to clipboard",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleGenerateToken = () => {
    const newToken = generateWebhookToken();
    saveWebhookToken();
  };

  const resetForm = () => {
    setEditingConfig(null);
    setFormName("");
    setSmtpServer("");
    setSmtpPort(587);
    setSmtpUsername("");
    setSmtpPassword("");
    setFromEmail("");
    setFromName("");
    setShowPassword(false);
  };

  const handleOpenAdd = () => {
    resetForm();
    onOpen();
  };

  const handleOpenEdit = (config: SmtpConfig) => {
    setEditingConfig(config);
    setFormName(config.name);
    setSmtpServer(config.smtpServer);
    setSmtpPort(config.smtpPort);
    setSmtpUsername(config.smtpUsername);
    setSmtpPassword(config.smtpPassword);
    setFromEmail(config.fromEmail);
    setFromName(config.fromName || "");
    setShowPassword(false);
    onOpen();
  };

  const handleSave = async () => {
    // Validation
    if (!formName.trim()) {
      toast({
        title: "Validation Error",
        description: "SMTP Name is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!smtpServer.trim()) {
      toast({
        title: "Validation Error",
        description: "SMTP Server is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!smtpPort || smtpPort < 1 || smtpPort > 65535) {
      toast({
        title: "Validation Error",
        description: "SMTP Port must be between 1 and 65535",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!smtpUsername.trim()) {
      toast({
        title: "Validation Error",
        description: "SMTP Username/Login is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!smtpPassword.trim()) {
      toast({
        title: "Validation Error",
        description: "SMTP Password/API Key is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!fromEmail.trim()) {
      toast({
        title: "Validation Error",
        description: "From Email is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(fromEmail.trim())) {
      toast({
        title: "Validation Error",
        description: "From Email must be a valid email address",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const configDocRef = doc(db, EMAIL_CONFIG_COLLECTION, "global");
      const configDoc = await getDoc(configDocRef);
      const existingData = configDoc.exists() ? (configDoc.data() as EmailConfigData) : {};
      let existingConfigs = existingData.smtpConfigs || [];

      // If no configs exist but we have a default in state, include it
      if (existingConfigs.length === 0 && smtpConfigs.length > 0) {
        // Use the default config from state if it exists
        const defaultFromState = smtpConfigs.find(c => c.id === "default_brevo_smtp");
        if (defaultFromState) {
          // If editing default or if no configs exist, use the default from state
          if (editingConfig?.id === "default_brevo_smtp" || !editingConfig) {
            existingConfigs = [defaultFromState];
          }
        }
      }

      let updatedConfigs: SmtpConfig[];
      let newActiveSmtpId = activeSmtpId;

      if (editingConfig) {
        // Check if config exists in array
        const configIndex = existingConfigs.findIndex((config) => config.id === editingConfig.id);
        
        if (configIndex >= 0) {
          // Update existing config
          updatedConfigs = existingConfigs.map((config) =>
            config.id === editingConfig.id
              ? {
                  ...config,
                  name: formName.trim(),
                  smtpServer: smtpServer.trim(),
                  smtpPort: smtpPort,
                  smtpUsername: smtpUsername.trim(),
                  smtpPassword: smtpPassword.trim(),
                  fromEmail: fromEmail.trim(),
                  fromName: fromName.trim() || "",
                  lastUpdated: Timestamp.now(),
                  updatedBy: currentUser?.email || "unknown",
                }
              : config
          );
        } else {
          // Config doesn't exist in array, add it (for default config case)
          const updatedConfig: SmtpConfig = {
            ...editingConfig,
            name: formName.trim(),
            smtpServer: smtpServer.trim(),
            smtpPort: smtpPort,
            smtpUsername: smtpUsername.trim(),
            smtpPassword: smtpPassword.trim(),
            fromEmail: fromEmail.trim(),
            fromName: fromName.trim() || "",
            lastUpdated: Timestamp.now(),
            updatedBy: currentUser?.email || "unknown",
            createdAt: editingConfig.createdAt || Timestamp.now(),
          };
          updatedConfigs = [...existingConfigs, updatedConfig];
        }
      } else {
        // Add new config
        const newId = `smtp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const newConfig: SmtpConfig = {
          id: newId,
          name: formName.trim(),
          smtpServer: smtpServer.trim(),
          smtpPort: smtpPort,
          smtpUsername: smtpUsername.trim(),
          smtpPassword: smtpPassword.trim(),
          fromEmail: fromEmail.trim(),
          fromName: fromName.trim() || "",
          isActive: existingConfigs.length === 0, // First config is active by default
          createdAt: Timestamp.now(),
          lastUpdated: Timestamp.now(),
          updatedBy: currentUser?.email || "unknown",
        };
        updatedConfigs = [...existingConfigs, newConfig];
        
        // If this is the first config, set it as active
        if (existingConfigs.length === 0) {
          newActiveSmtpId = newId;
        }
      }

      // Update active SMTP ID if needed
      if (newActiveSmtpId && newActiveSmtpId !== activeSmtpId) {
        setActiveSmtpId(newActiveSmtpId);
      }

      // Save to Firestore - use setDoc without merge to ensure array is properly saved
      await setDoc(
        configDocRef,
        {
          smtpConfigs: updatedConfigs,
          activeSmtpId: newActiveSmtpId || activeSmtpId,
          lastUpdated: serverTimestamp(),
        }
      );

      await loadConfigs();
      resetForm();
      onClose();

      toast({
        title: "Success",
        description: editingConfig ? "SMTP configuration updated successfully" : "SMTP configuration added successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Error saving email config:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save email configuration",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSetActive = async (configId: string) => {
    if (configId === activeSmtpId) return;

    setLoading(true);
    try {
      const configDocRef = doc(db, EMAIL_CONFIG_COLLECTION, "global");
      await setDoc(
        configDocRef,
        {
          activeSmtpId: configId,
          lastUpdated: serverTimestamp(),
        },
        { merge: true }
      );

      setActiveSmtpId(configId);
      await loadConfigs();

      toast({
        title: "Success",
        description: "Active SMTP configuration updated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Error setting active SMTP:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to set active SMTP",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (configId: string) => {
    if (configId === activeSmtpId) {
      toast({
        title: "Error",
        description: "Cannot delete the active SMTP configuration. Please set another one as active first.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const configDocRef = doc(db, EMAIL_CONFIG_COLLECTION, "global");
      const configDoc = await getDoc(configDocRef);
      const existingData = configDoc.exists() ? (configDoc.data() as EmailConfigData) : {};
      const existingConfigs = existingData.smtpConfigs || [];

      const updatedConfigs = existingConfigs.filter((config) => config.id !== configId);

      await setDoc(
        configDocRef,
        {
          smtpConfigs: updatedConfigs,
          lastUpdated: serverTimestamp(),
        },
        { merge: true }
      );

      await loadConfigs();

      toast({
        title: "Success",
        description: "SMTP configuration deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Error deleting SMTP config:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete SMTP configuration",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProviderChange = async (next: "ses" | "brevo") => {
    if (next === emailProvider || savingProvider) return false;
    setSavingProvider(true);
    try {
      await setDoc(
        doc(db, EMAIL_CONFIG_COLLECTION, "global"),
        { emailProvider: next, lastUpdated: serverTimestamp() },
        { merge: true }
      );
      setEmailProvider(next);
      toast({
        title: next === "ses" ? "Amazon SES is active" : "Brevo is active",
        description: "Order emails, reservation emails, and test emails use this provider.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      return true;
    } catch (error: any) {
      toast({
        title: "Email provider not saved",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    } finally {
      setSavingProvider(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (emailProvider === "brevo" && !activeConfig) {
      toast({
        title: "Error",
        description: "No active SMTP configuration found",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!testEmail.trim()) {
      toast({
        title: "Validation Error",
        description: "Recipient email is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testEmail.trim())) {
      toast({
        title: "Validation Error",
        description: "Please enter a valid email address",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (emailProvider === "brevo" && (!activeConfig?.smtpPassword || !activeConfig?.fromEmail)) {
      toast({
        title: "Error",
        description: "SMTP configuration is incomplete. Please ensure API key and From Email are set.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSendingTest(true);
    try {
      const sendConfiguredEmail = httpsCallable<any, { ok: boolean; message?: string; provider?: string }>(functions, "sendConfiguredEmail");
      const htmlContent = `
            <html>
              <head></head>
              <body>
                <p>Hello,</p>
                <p>This is a test email to verify the email provider is working correctly.</p>
                <p><strong>Test Message:</strong> ${testMessage || "No message provided"}</p>
                <p>Best regards,<br/>RMS POS System</p>
              </body>
            </html>
          `;
      const { data } = await sendConfiguredEmail({
        to: testEmail.trim(),
        toName: "Test Recipient",
        subject: "Test Email",
        html: htmlContent,
        text: `Hello,\n\nThis is a test email.\n\nTest Message: ${testMessage || "No message provided"}\n`,
      });
      if (!data.ok) {
        throw new Error(data.message || "Email was not sent");
      }

      toast({
        title: "Success",
        description: `Test email sent to ${testEmail}`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      setTestEmail("");
      setTestMessage("This is a test email to verify SMTP configuration.");
    } catch (error: any) {
      toast({
        title: "Error Sending Email",
        description: error.message || "Failed to send test email.",
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setSendingTest(false);
    }
  };

  const activeConfig = smtpConfigs.find((config) => config.id === activeSmtpId);

  return (
    <PageShell
      title="Email"
      description="Choose Amazon SES or Brevo for store and customer email."
      wide
      actions={
        providerTab === 1 && activeTab === 0 ? (
          <Button leftIcon={<AddIcon />} onClick={handleOpenAdd}>
            Generate a new SMTP key
          </Button>
        ) : undefined
      }
    >
      <VStack spacing={6} align="stretch">

        <Tabs index={providerTab} onChange={setProviderTab}>
          <TabList>
            <Tab>
              Amazon SES
              {emailProvider === "ses" ? <Badge ml={2} colorScheme="green">Active</Badge> : null}
            </Tab>
            <Tab>
              Brevo
              {emailProvider === "brevo" ? <Badge ml={2} colorScheme="green">Active</Badge> : null}
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0} pt={4}>
              <VStack spacing={6} align="stretch">
              <Box bg={bgCard} borderRadius="lg" boxShadow="sm" border="1px" borderColor={borderColor} p={6}>
                <HStack justify="space-between" mb={4}>
                  <Text fontSize="lg" fontWeight="semibold" color={textColor}>Amazon SES</Text>
                  {emailProvider === "ses" ? (
                    <Badge colorScheme="green">Active</Badge>
                  ) : (
                    <Button size="sm" onClick={() => { setPendingProvider("ses"); setConfirmWord(""); }}>
                      Set active
                    </Button>
                  )}
                </HStack>
                <Text fontSize="sm" color={textSecondary} mb={4}>
                  These fields are the sender account. Nuage POS uses them to send mail. They do not track bounces.
                </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel>Region</FormLabel>
                <Select value={sesRegion} onChange={(event) => setSesRegion(event.target.value)} bg="white">
                  {["ca-central-1", "us-east-1", "us-east-2", "us-west-1", "us-west-2", "eu-west-1", "eu-west-2", "eu-west-3", "eu-central-1"].map((region) => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Access key ID</FormLabel>
                <Input value={sesAccessKeyId} onChange={(event) => setSesAccessKeyId(event.target.value)} placeholder="AKIA..." bg="white" autoComplete="off" />
              </FormControl>
              <FormControl>
                <FormLabel>Secret access key</FormLabel>
                <InputGroup>
                  <Input type={showSesSecret ? "text" : "password"} value={sesSecret} onChange={(event) => setSesSecret(event.target.value)} bg="white" autoComplete="new-password" />
                  <InputRightElement width="4.5rem">
                    <Button h="1.75rem" size="sm" onClick={() => setShowSesSecret((value) => !value)}>
                      {showSesSecret ? "Hide" : "Show"}
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              <FormControl>
                <FormLabel>Sender address</FormLabel>
                <Input type="email" value={sesFromAddress} onChange={(event) => setSesFromAddress(event.target.value)} placeholder="receipts@nuagepos.com" bg="white" />
              </FormControl>
              <FormControl>
                <FormLabel>Sender name</FormLabel>
                <Input value={sesFromName} onChange={(event) => setSesFromName(event.target.value)} bg="white" />
              </FormControl>
              <FormControl>
                <FormLabel>Reply-to address</FormLabel>
                <Input type="email" value={sesReplyTo} onChange={(event) => setSesReplyTo(event.target.value)} bg="white" />
              </FormControl>
            </SimpleGrid>
            <Button mt={4} onClick={handleSaveSes} isLoading={savingSes}>
              Save Amazon SES
            </Button>
              </Box>

              <Box bg={bgCard} borderRadius="lg" boxShadow="sm" border="1px" borderColor={borderColor} p={6}>
                <Text fontSize="lg" fontWeight="semibold" color={textColor} mb={3}>
                  Email bounce tracking
                </Text>
                <VStack align="stretch" spacing={3} mb={5}>
                  <Text fontSize="sm" color={textColor}>
                    This setting is separate from the sender account above. It tells Amazon SES where to report a receipt email that could not be delivered.
                  </Text>
                  <Text fontSize="sm" color={textColor}>
                    Nuage POS sends the temporary bill, the closing receipt, and later changes of those bills by email. When the address is rejected, Amazon reports the bounce to Nuage POS. The register keeps a notice on screen until the cashier corrects the address and sends the receipt again.
                  </Text>
                  <Text fontSize="sm" color={textColor}>
                    Application: Nuage POS receipt email. Amazon SES configuration set nuage-pos-receipts. Bounce receiver: Cloud Function sesReceiptBounce in Firebase project fiverrrms1, region northamerica-northeast1.
                  </Text>
                  <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                    To set this up again in Amazon SES
                  </Text>
                  <Text fontSize="sm" color={textColor}>
                    1. Open Amazon SES in the same region as the sender account above.
                  </Text>
                  <Text fontSize="sm" color={textColor}>
                    2. Create a configuration set named nuage-pos-receipts. Turn off email feedback forwarding on that configuration set. The bounce is handled in Nuage POS. Amazon does not also mail the bounce to the sender address.
                  </Text>
                  <Text fontSize="sm" color={textColor}>
                    3. Create an Amazon SNS topic for these receipt bounces.
                  </Text>
                  <Text fontSize="sm" color={textColor}>
                    4. On the configuration set, add an event destination. Publish Bounce, Complaint, and Reject to that topic.
                  </Text>
                  <Text fontSize="sm" color={textColor}>
                    5. Create an HTTPS subscription on the topic. Use the bounce URL below. Amazon sends a confirmation request, and Nuage POS confirms the subscription.
                  </Text>
                  <Text fontSize="sm" color={textColor}>
                    6. Enter that configuration set name in the field below and save. Nuage POS places the name on every receipt email, so Amazon sends the bounce events for those messages to this URL.
                  </Text>
                </VStack>
                <FormControl mb={4}>
                  <FormLabel>Bounce URL</FormLabel>
                  <InputGroup>
                    <Input
                      value="https://northamerica-northeast1-fiverrrms1.cloudfunctions.net/sesReceiptBounce"
                      readOnly
                      bg="gray.50"
                      fontFamily="mono"
                      fontSize="sm"
                    />
                    <InputRightElement width="4.5rem">
                      <IconButton
                        h="1.75rem"
                        size="sm"
                        aria-label="Copy bounce URL"
                        icon={<CopyIcon />}
                        onClick={() => {
                          navigator.clipboard.writeText("https://northamerica-northeast1-fiverrrms1.cloudfunctions.net/sesReceiptBounce");
                          toast({ title: "Bounce URL copied", status: "success", duration: 2000, isClosable: true });
                        }}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <Text fontSize="sm" color={textSecondary} mt={1}>
                    This address is fixed. It is the Nuage POS receiver. Copy it into the Amazon SNS subscription.
                  </Text>
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Configuration set</FormLabel>
                  <Input value={sesConfigurationSet} onChange={(event) => setSesConfigurationSet(event.target.value)} placeholder="nuage-pos-receipts" bg="white" />
                  <Text fontSize="sm" color={textSecondary} mt={1}>
                    Must match the configuration set name in Amazon SES. The current name is nuage-pos-receipts.
                  </Text>
                </FormControl>
                <Button onClick={handleSaveBounce} isLoading={savingBounce}>
                  Save email bounce tracking
                </Button>
              </Box>
              </VStack>
            </TabPanel>
            <TabPanel px={0} pt={4}>
              <HStack justify="flex-end" mb={4}>
                {emailProvider === "brevo" ? (
                  <Badge colorScheme="green">Active</Badge>
                ) : (
                  <Button size="sm" onClick={() => { setPendingProvider("brevo"); setConfirmWord(""); }}>
                    Set active
                  </Button>
                )}
              </HStack>
        <Tabs index={activeTab} onChange={setActiveTab}>
          <TabList borderBottom="2px" borderColor={borderColor}>
            <Tab
              _selected={{
                color: "purple.600",
                borderBottomColor: "purple.600",
                borderBottomWidth: "2px",
              }}
              fontWeight="semibold"
            >
              SMTP
            </Tab>
            <Tab
              _selected={{
                color: "purple.600",
                borderBottomColor: "purple.600",
                borderBottomWidth: "2px",
              }}
              fontWeight="semibold"
            >
              Send Test Email
            </Tab>
          </TabList>

          <TabPanels>
            {/* SMTP Tab */}
            <TabPanel px={0} pt={6}>
              <VStack spacing={6} align="stretch">
                {/* Active SMTP Settings Display */}
                {activeConfig && (
                  <Box bg={bgCard} borderRadius="lg" boxShadow="sm" border="1px" borderColor={borderColor} p={6}>
                    <Text fontSize="lg" fontWeight="semibold" mb={4} color={textColor}>
                      Your SMTP Settings
                    </Text>
                    <VStack spacing={3} align="stretch">
                      <HStack>
                        <Text fontWeight="medium" color={textSecondary} minW="120px">
                          SMTP Server:
                        </Text>
                        <Text color={textColor}>{activeConfig.smtpServer}</Text>
                      </HStack>
                      <HStack>
                        <Text fontWeight="medium" color={textSecondary} minW="120px">
                          Port:
                        </Text>
                        <Text color={textColor}>{activeConfig.smtpPort}</Text>
                      </HStack>
                      <HStack>
                        <Text fontWeight="medium" color={textSecondary} minW="120px">
                          Login:
                        </Text>
                        <Text color={textColor}>{activeConfig.smtpUsername}</Text>
                      </HStack>
                    </VStack>
                  </Box>
                )}

                {/* SMTP Keys Table */}
                <Box bg={bgCard} borderRadius="lg" boxShadow="sm" border="1px" borderColor={borderColor} p={6}>
                  <Text fontSize="lg" fontWeight="semibold" mb={4} color={textColor}>
                    Your SMTP Keys
                  </Text>

                  {smtpConfigs.length === 0 ? (
                    <Box textAlign="center" py={8}>
                      <Text color={textSecondary} mb={4}>
                        No SMTP configurations found. Create your first SMTP key to get started.
                      </Text>
                      <Button colorScheme="blue" onClick={handleOpenAdd}>
                        Add SMTP Key
                      </Button>
                    </Box>
                  ) : (
                    <Box overflowX="auto">
                      <Table variant="simple">
                        <Thead>
                          <Tr>
                            <Th>SMTP key value</Th>
                            <Th>SMTP key name</Th>
                            <Th>Status</Th>
                            <Th>Created on</Th>
                            <Th>Last used on</Th>
                            <Th>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {smtpConfigs.map((config) => {
                            const isActive = config.id === activeSmtpId;
                            const maskedPassword = config.smtpPassword && config.smtpPassword.length > 0
                              ? `${"*".repeat(Math.min(config.smtpPassword.length - 6, 10))}${config.smtpPassword.slice(-6)}`
                              : "**********";
                            
                            return (
                              <Tr key={config.id}>
                                <Td>
                                  <Text fontFamily="mono" fontSize="sm">
                                    {maskedPassword}
                                  </Text>
                                </Td>
                                <Td>{config.name}</Td>
                                <Td>
                                  <Badge
                                    colorScheme={isActive ? "green" : "gray"}
                                    px={3}
                                    py={1}
                                    borderRadius="full"
                                  >
                                    {isActive ? "Active" : "Inactive"}
                                  </Badge>
                                </Td>
                                <Td>
                                  {config.createdAt
                                    ? (() => {
                                        try {
                                          const date = config.createdAt?.toDate 
                                            ? config.createdAt.toDate() 
                                            : config.createdAt instanceof Date 
                                            ? config.createdAt 
                                            : new Date(config.createdAt);
                                          return date.toLocaleDateString("en-US", {
                                            year: "numeric",
                                            month: "long",
                                            day: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          });
                                        } catch {
                                          return "--";
                                        }
                                      })()
                                    : "--"}
                                </Td>
                                <Td>--</Td>
                                <Td>
                                  <HStack spacing={2}>
                                    {!isActive && (
                                      <Button
                                        size="sm"
                                        colorScheme="blue"
                                        variant="ghost"
                                        onClick={() => handleSetActive(config.id)}
                                        isLoading={loading}
                                      >
                                        Set Active
                                      </Button>
                                    )}
                                    <IconButton
                                      aria-label="Edit"
                                      icon={<EditIcon />}
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleOpenEdit(config)}
                                    />
                                    <IconButton
                                      aria-label="Delete"
                                      icon={<DeleteIcon />}
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDelete(config.id)}
                                      isDisabled={isActive}
                                    />
                                  </HStack>
                                </Td>
                              </Tr>
                            );
                          })}
                        </Tbody>
                      </Table>
                    </Box>
                  )}
                </Box>
              </VStack>
            </TabPanel>

            {/* Send Test Email Tab */}
            <TabPanel px={0} pt={6}>
              <Box bg={bgCard} borderRadius="lg" boxShadow="sm" border="1px" borderColor={borderColor} p={6}>
                <Text fontSize="lg" fontWeight="semibold" mb={4} color={textColor}>
                  Send Test Email
                </Text>
                
                {!activeConfig ? (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription>
                      No active SMTP configuration found. Please configure an SMTP key first.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <VStack spacing={4} align="stretch">
                    <Alert status="info" borderRadius="md">
                      <AlertIcon />
                      <AlertDescription>
                        Send a test email to verify your SMTP configuration is working correctly.
                        The email will be sent using the active SMTP configuration.
                      </AlertDescription>
                    </Alert>

                    <FormControl isRequired>
                      <FormLabel>Recipient Email</FormLabel>
                      <Input
                        type="email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="recipient@example.com"
                        bg="white"
                      />
                      <Text fontSize="xs" color={textSecondary} mt={1}>
                        Enter the email address where you want to receive the test email
                      </Text>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Test Message</FormLabel>
                      <Input
                        type="text"
                        value={testMessage}
                        onChange={(e) => setTestMessage(e.target.value)}
                        placeholder="Enter a test message"
                        bg="white"
                      />
                      <Text fontSize="xs" color={textSecondary} mt={1}>
                        A short message to include in the test email
                      </Text>
                    </FormControl>

                    <Button
                      colorScheme="blue"
                      onClick={handleSendTestEmail}
                      isLoading={sendingTest}
                      loadingText="Sending..."
                      isDisabled={!testEmail.trim() || !activeConfig.smtpPassword || !activeConfig.fromEmail}
                    >
                      Send Test Email
                    </Button>

                    <Divider my={4} />

                    <Box>
                      <Text fontSize="md" fontWeight="semibold" mb={3} color={textColor}>
                        Webhook Configuration
                      </Text>
                      <Alert status="info" borderRadius="md" mb={4}>
                        <AlertIcon />
                        <AlertDescription>
                          Configure this webhook URL in your Brevo dashboard to receive bounce and error notifications.
                        </AlertDescription>
                      </Alert>

                      <FormControl mb={4}>
                        <FormLabel>Webhook Endpoint URL</FormLabel>
                        <InputGroup>
                          <Input
                            value={webhookUrl}
                            readOnly
                            bg="gray.50"
                            fontFamily="mono"
                            fontSize="sm"
                          />
                          <InputRightElement width="4.5rem">
                            <IconButton
                              h="1.75rem"
                              size="sm"
                              aria-label="Copy webhook URL"
                              icon={<CopyIcon />}
                              onClick={handleCopyWebhookUrl}
                            />
                          </InputRightElement>
                        </InputGroup>
                        <Text fontSize="xs" color={textSecondary} mt={1}>
                          Configure this URL in your Brevo Dashboard under Settings → Webhooks → Transactional webhooks
                        </Text>
                      </FormControl>

                      <FormControl mb={4}>
                        <FormLabel>Webhook Token</FormLabel>
                        <InputGroup>
                          <Input
                            type={showWebhookToken ? "text" : "password"}
                            value={webhookToken}
                            onChange={(e) => setWebhookToken(e.target.value)}
                            placeholder="Generate a token to secure your webhook"
                            bg="white"
                            fontFamily="mono"
                            fontSize="sm"
                          />
                          <InputRightElement width="8rem">
                            <HStack spacing={1}>
                              <IconButton
                                h="1.75rem"
                                size="sm"
                                aria-label={showWebhookToken ? "Hide webhook token" : "Show webhook token"}
                                icon={showWebhookToken ? <ViewOffIcon /> : <ViewIcon />}
                                onClick={() => setShowWebhookToken(!showWebhookToken)}
                              />
                              <Button
                                size="sm"
                                h="1.75rem"
                                onClick={handleGenerateToken}
                              >
                                Generate
                              </Button>
                            </HStack>
                          </InputRightElement>
                        </InputGroup>
                        <Text fontSize="xs" color={textSecondary} mt={1}>
                          Token for webhook verification. Generate a new token and save it.
                        </Text>
                      </FormControl>

                      {webhookToken && (
                        <Button
                          colorScheme="green"
                          onClick={saveWebhookToken}
                          size="sm"
                        >
                          Save Token
                        </Button>
                      )}
                    </Box>
                  </VStack>
                )}
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
            </TabPanel>
          </TabPanels>
        </Tabs>

        <Modal isOpen={pendingProvider !== null} onClose={() => setPendingProvider(null)} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Change the active email provider</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack align="stretch" spacing={4}>
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>
                    Have you tested this provider and confirmed the email is working? Changing the active provider interrupts normal email. Orders, reservations, and other messages will use {pendingProvider === "brevo" ? "Brevo" : "Amazon SES"}.
                  </AlertDescription>
                </Alert>
                <FormControl>
                  <FormLabel>Type change to continue</FormLabel>
                  <Input value={confirmWord} onChange={(event) => setConfirmWord(event.target.value)} placeholder="change" bg="white" autoComplete="off" />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={() => setPendingProvider(null)}>Cancel</Button>
              <Button
                colorScheme="red"
                isDisabled={confirmWord.trim() !== "change"}
                isLoading={savingProvider}
                onClick={async () => {
                  if (!pendingProvider || confirmWord.trim() !== "change") return;
                  const saved = await handleProviderChange(pendingProvider);
                  if (!saved) return;
                  setPendingProvider(null);
                  setConfirmWord("");
                }}
              >
                Change
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Add/Edit Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              {editingConfig ? "Edit SMTP Configuration" : "Add New SMTP Configuration"}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel>SMTP Key Name</FormLabel>
                  <Input
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="e.g., Master Password, Production SMTP"
                    bg="white"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>SMTP Server</FormLabel>
                  <Input
                    value={smtpServer}
                    onChange={(e) => setSmtpServer(e.target.value)}
                    placeholder="smtp-relay.brevo.com"
                    bg="white"
                  />
                  <Text fontSize="xs" color={textSecondary} mt={1}>
                    The hostname of your SMTP server (e.g., smtp-relay.brevo.com, smtp.gmail.com)
                  </Text>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>SMTP Port</FormLabel>
                  <NumberInput
                    value={smtpPort}
                    onChange={(_, valueAsNumber) => setSmtpPort(valueAsNumber || 587)}
                    min={1}
                    max={65535}
                    bg="white"
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="xs" color={textSecondary} mt={1}>
                    Common ports: 587 (TLS), 465 (SSL), 25 (unencrypted)
                  </Text>
                </FormControl>

                <Divider />

                <FormControl isRequired>
                  <FormLabel>SMTP Login/Username</FormLabel>
                  <Input
                    value={smtpUsername}
                    onChange={(e) => setSmtpUsername(e.target.value)}
                    placeholder="891b00001@smtp-brevo.com"
                    bg="white"
                  />
                  <Text fontSize="xs" color={textSecondary} mt={1}>
                    Your SMTP authentication username/login
                  </Text>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>SMTP Password/API Key</FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={smtpPassword}
                      onChange={(e) => setSmtpPassword(e.target.value)}
                      placeholder="Enter your SMTP password or API key"
                      bg="white"
                    />
                    <InputRightElement width="4.5rem">
                      <IconButton
                        h="1.75rem"
                        size="sm"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <Text fontSize="xs" color={textSecondary} mt={1}>
                    For Brevo, use your SMTP API key as the password
                  </Text>
                </FormControl>

                <Divider />

                <FormControl isRequired>
                  <FormLabel>From Email Address</FormLabel>
                  <Input
                    type="email"
                    value={fromEmail}
                    onChange={(e) => setFromEmail(e.target.value)}
                    placeholder="noreply@example.com"
                    bg="white"
                  />
                  <Text fontSize="xs" color={textSecondary} mt={1}>
                    The email address that will appear as the sender
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>From Name (Optional)</FormLabel>
                  <Input
                    value={fromName}
                    onChange={(e) => setFromName(e.target.value)}
                    placeholder="Your Company Name"
                    bg="white"
                  />
                  <Text fontSize="xs" color={textSecondary} mt={1}>
                    The display name that will appear as the sender
                  </Text>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSave}
                isLoading={loading}
                loadingText="Saving..."
              >
                {editingConfig ? "Update" : "Create"} SMTP Key
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </PageShell>
  );
};

export default EmailSettings;
